import { useState } from "react";
import { Medal, Award, FileBadge, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal, SectionHeading } from "./Reveal";
import { AWARDS } from "@/lib/site";

export const Awards = () => {
  const [viewer, setViewer] = useState(null);

  return (
    <section id="awards" data-testid="awards-recognition-section" className="relative overflow-hidden bg-[#0F2440] py-20 lg:py-32">
      <span className="font-heading text-outline-green absolute -left-4 bottom-0 select-none text-[9rem] font-bold leading-none opacity-60 sm:text-[14rem]" aria-hidden="true">
        सम्मान
      </span>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading
          testId="awards-heading"
          overline="Awards & National Recognition"
          title="Honoured to serve, recognised nationally"
          description="Each recognition belongs as much to our students and the island community as it does to the center."
        />
        <div className="mt-14 max-w-4xl space-y-6">
          {AWARDS.map((award, i) => {
            const Icon = award.highlight ? Award : Medal;
            return (
              <Reveal key={award.title} delay={0.07 * i}>
                <div
                  data-testid={`award-item-${i}`}
                  className={`group flex flex-col gap-4 rounded-2xl border p-6 backdrop-blur transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-[#D97706]/40 hover:bg-white/10 sm:flex-row sm:items-center sm:gap-6 sm:p-7 ${
                    award.highlight ? "border-[#D97706]/40 bg-[#D97706]/10" : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#D97706]/15 text-[#D97706] transition-colors duration-300 group-hover:bg-[#D97706] group-hover:text-white">
                    <Icon className="h-7 w-7" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D97706]">{award.year}</p>
                      {award.highlight && (
                        <span className="rounded-full bg-[#D97706] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-white">Latest</span>
                      )}
                    </div>
                    <h3 className="font-heading mt-1 text-xl font-semibold text-[#F8FFF8] sm:text-2xl">{award.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{award.detail}</p>
                  </div>
                  {award.certificate && (
                    <button
                      data-testid={`award-certificate-button-${i}`}
                      onClick={() => setViewer(award)}
                      className="relative shrink-0 overflow-hidden rounded-xl border border-white/20 transition-[transform,border-color] duration-300 hover:scale-[1.03] hover:border-[#D97706]"
                      aria-label={`View certificate: ${award.title}`}
                    >
                      <img src={award.certificate} alt={`Certificate — ${award.title}`} loading="lazy" className="h-28 w-20 object-cover object-top sm:h-32 sm:w-24" />
                      <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-slate-900/70 py-1 text-[9px] font-bold uppercase tracking-wider text-white backdrop-blur">
                        <Maximize2 className="h-3 w-3" />
                        View
                      </span>
                    </button>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Dialog open={!!viewer} onOpenChange={() => setViewer(null)}>
        <DialogContent data-testid="award-certificate-modal" className="max-w-lg rounded-3xl border-emerald-900/10 bg-white p-4">
          {viewer && (
            <>
              <DialogTitle className="sr-only">{viewer.title}</DialogTitle>
              <img src={viewer.certificate} alt={`Certificate — ${viewer.title}`} className="max-h-[78vh] w-full rounded-2xl object-contain" />
              <p className="flex items-center justify-center gap-2 px-2 pb-2 pt-3 text-center text-sm font-semibold text-slate-800">
                <FileBadge className="h-4 w-4 text-[#D97706]" />
                {viewer.title}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
